import { FeedItemToken, IFeedItemToken } from '@app/models/FeedItemToken'
import {
  IMentionEditorSuggestions,
  MentionEditorSuggestions,
} from '@app/models/MentionEditorSuggestions'
import { ApplicationState } from '@app/store'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Map, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IMentionEditorState {
  readonly editors: Map<string, IMentionEditorSuggestions>
}

const MentionEditorStateRecord = Record({
  editors: Map<string, MentionEditorSuggestions>(),
  quickAdd: undefined,
})

export class MentionEditorState extends MentionEditorStateRecord
  implements IMentionEditorState {
  public static fromJS(initialState?: Partial<IMentionEditorState>) {
    return new MentionEditorState(initialState)
  }

  public readonly editors: Map<string, MentionEditorSuggestions>

  constructor(props?: Partial<IMentionEditorState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------
export enum ActionTypes {
  ClearSuggestions = 'MENTION_EDITOR::CLEAR_SUGGESTIONS',
  FetchSuggestions = 'MENTION_EDITOR::FETCH_SUGGESTIONS',
  FetchSuggestionsFailure = 'MENTION_EDITOR::FETCH_SUGGESTIONS_FAILURE',
  FetchSuggestionsRequest = 'MENTION_EDITOR::FETCH_SUGGESTIONS_REQUEST',
  FetchSuggestionsSuccess = 'MENTION_EDITOR::FETCH_SUGGESTIONS_SUCCESS',
  InitEditor = 'MENTION_EDITOR::INIT_EDITOR',
  RemoveEditor = 'MENTION_EDITOR::REMOVE_EDITOR',
}

interface IClearSuggestionsAction {
  editorId: string
  type: ActionTypes.ClearSuggestions
}

interface IFetchSuggestionsAction {
  editorId: string
  filter: string
  type: ActionTypes.FetchSuggestions
}

interface IFetchSuggestionsFailureAction {
  editorId: string
  error: string
  type: ActionTypes.FetchSuggestionsFailure
}

interface IFetchSuggestionsRequestAction {
  editorId: string
  filter: string
  type: ActionTypes.FetchSuggestionsRequest
}

interface IFetchSuggestionsSuccessAction {
  editorId: string
  suggestions: IFeedItemToken[]
  type: ActionTypes.FetchSuggestionsSuccess
}

interface IInitEditorAction {
  editorId: string
  type: ActionTypes.InitEditor
}

interface IRemoveEditorAction {
  editorId: string
  type: ActionTypes.RemoveEditor
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IClearSuggestionsAction
  | IFetchSuggestionsFailureAction
  | IFetchSuggestionsRequestAction
  | IFetchSuggestionsSuccessAction
  | IInitEditorAction
  | IRemoveEditorAction

// -------------------------
// ACTION CREATORS
// -------------------------

function clearSuggestions(editorId: string): IClearSuggestionsAction {
  return {
    editorId,
    type: ActionTypes.ClearSuggestions,
  }
}

function fetchSuggestions(
  editorId: string,
  filter: string,
): IFetchSuggestionsAction {
  return {
    editorId,
    filter,
    type: ActionTypes.FetchSuggestions,
  }
}

function fetchSuggestionsFailure(
  editorId: string,
  error: string,
): IFetchSuggestionsFailureAction {
  return {
    editorId,
    error,
    type: ActionTypes.FetchSuggestionsFailure,
  }
}

function fetchSuggestionsRequest(
  editorId: string,
  filter: string,
): IFetchSuggestionsRequestAction {
  return {
    editorId,
    filter,
    type: ActionTypes.FetchSuggestionsRequest,
  }
}

function fetchSuggestionsSuccess(
  editorId: string,
  suggestions: IFeedItemToken[],
): IFetchSuggestionsSuccessAction {
  return {
    editorId,
    suggestions,
    type: ActionTypes.FetchSuggestionsSuccess,
  }
}

function initEditor(editorId: string): IInitEditorAction {
  return {
    editorId,
    type: ActionTypes.InitEditor,
  }
}

function removeEditor(editorId: string): IRemoveEditorAction {
  return {
    editorId,
    type: ActionTypes.RemoveEditor,
  }
}

export const actionCreators = {
  clearSuggestions,
  fetchSuggestions,
  initEditor,
  removeEditor,
}

// -------------------------
// REDUCER
// -------------------------
export const reducer: Reducer<MentionEditorState> = (
  state: MentionEditorState = new MentionEditorState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.ClearSuggestions:
      if (state.hasIn(['editors', action.editorId])) {
        return state.setIn(
          ['editors', action.editorId, 'suggestions'],
          List<IFeedItemToken>(),
        ) as MentionEditorState
      } else {
        return state
      }

    case ActionTypes.FetchSuggestionsFailure:
      if (state.hasIn(['editors', action.editorId])) {
        return state
          .setIn(['editors', action.editorId, 'error'], action.error)
          .setIn(
            ['editors', action.editorId, 'isFetching'],
            false,
          ) as MentionEditorState
      } else {
        return state
      }

    case ActionTypes.FetchSuggestionsRequest:
      if (state.hasIn(['editors', action.editorId])) {
        return state
          .setIn(['editors', action.editorId, 'isFetching'], true)
          .setIn(
            ['editors', action.editorId, 'searchFilter'],
            action.filter,
          ) as MentionEditorState
      } else {
        return state
      }

    case ActionTypes.FetchSuggestionsSuccess:
      if (state.hasIn(['editors', action.editorId])) {
        return state
          .setIn(['editors', action.editorId, 'isFetching'], false)
          .setIn(
            ['editors', action.editorId, 'suggestions'],
            List(action.suggestions),
          ) as MentionEditorState
      } else {
        return state
      }

    case ActionTypes.InitEditor:
      return state.setIn(
        ['editors', action.editorId],
        new MentionEditorSuggestions(),
      ) as MentionEditorState

    case ActionTypes.RemoveEditor:
      return state.deleteIn(['editors', action.editorId]) as MentionEditorState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* fetchSuggestionsSaga(action: IFetchSuggestionsAction) {
  try {
    yield put(fetchSuggestionsRequest(action.editorId, action.filter))

    if (!action.filter || action.filter.length < 2) {
      yield put(fetchSuggestionsSuccess(action.editorId, []))
      return
    }

    const url = `/api/FeedItems/Tokens?filter=${encodeURIComponent(
      action.filter,
    )}`

    const result = yield makeAuthenticatedRequest(url)
    if (Array.isArray(result)) {
      const suggestions = result.map((r) => FeedItemToken.fromJS(r))
      yield put(fetchSuggestionsSuccess(action.editorId, suggestions))
    } else {
      yield put(
        fetchSuggestionsFailure(action.editorId, 'Failed to get suggestions'),
      )
    }
  } catch (err) {
    yield put(fetchSuggestionsFailure(action.editorId, err.message))
  }
}

function* watchFetchSuggestions() {
  yield takeLatest(ActionTypes.FetchSuggestions, fetchSuggestionsSaga)
}

export function* mentionEditorRootSaga() {
  yield fork(watchFetchSuggestions)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.mentionEditor

const selectEditor = (editorId: string) =>
  createSelector(
    selectModuleState(),
    (mentionEditorState) =>
      mentionEditorState.getIn(['editors', editorId]) as
        | MentionEditorSuggestions
        | undefined,
  )

export const selectSuggestions = (editorId: string) =>
  createSelector(
    selectEditor(editorId),
    (editor) => (editor ? editor.suggestions : List<FeedItemToken>()),
  )
